import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { render } from 'react-dom';
import axios from '../../axios';
import SearchForm from '../../organisms/SearchForm';
import { http_status_code_list, search_params_init } from '../../Const';
import {
  SearchParams,
  DatatableConvertedData,
  ValidMessage,
  Options,
} from '../../types/ProjectListStaff';
import { handleBrowserBack } from '../../BrowserBack';
import { parse } from 'querystring';
import { scrollPageTop } from '../../Scroll';
import { jumpUrl } from '../../Common';
import StaffNavbar from '../../molecules/StaffNavbar';
import MDBDatatable from '../../organisms/MDBDatatable';
import { convertMDBDatatableRowData } from '../../Common';
import { store_in_charge_default } from '../../const/ProjectList';
import Moment from 'moment';

const ProjectListStaff: React.FC = () => {
  const [projects, setProjects] = useState<DatatableConvertedData[]>([]);
  const [is_loading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<Options>({
    dealer: [{ key: '', value: '' }],
    preliminary_decision: [{ key: '', value: '' }],
    status: [{ key: '', value: '' }],
    loan_item: [{ key: '', value: '' }],
  });
  // 検索パラメータの初期化
  const [search_params, setSearchParams] = useState<SearchParams>(search_params_init);
  const [store_in_charge, setStoreInCharge] = useState('');
  // Ajaxリクエスト中フラグ
  const [is_connecting, setIsConnecting] = useState(false);
  const [valid_message, setValidMessage] = useState<ValidMessage>();

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
    const getProject = async () => {
      try {
        //ローディング画面表示
        setIsLoading(true);
        const response = await axios.get(
          `${location.origin}${process.env.MIX_MOCK_URL}/api/projects/staff`
        );
        const newProjects: DatatableConvertedData[] = convertMDBDatatableRowData(
          response.data.projects
        );
        setProjects(newProjects);
        setOptions(response.data.options);
        // 検索条件の初期値を更新
        const newSearchParams = search_params;

        // ユーザの店舗IDが担当店舗リストに含まれているかをチェックする
        const dealer_list = response.data.options.dealer.map((dealer: any) => dealer.key);
        const new_store_in_charge = dealer_list.includes(
          String(response.data.options.user.office_id)
        )
          ? String(response.data.options.user.office_id)
          : store_in_charge_default;

        newSearchParams['store_in_charge'] = new_store_in_charge;
        setSearchParams(newSearchParams);
        setStoreInCharge(new_store_in_charge);
      } catch (error) {
        console.error(error.message);
      } finally {
        //画面トップに遷移
        scrollPageTop();
        //ローディング画面非表示
        setIsLoading(false);
      }
    };

    // 案件を検索する
    const searchProject = async (params: SearchParams) => {
      try {
        //ローディング画面表示
        setIsLoading(true);
        const response = await axios.post(
          `${location.origin}${process.env.MIX_MOCK_URL}/api/projects/staff/search`,
          params
        );
        const newProjects = response.data.projects;
        setProjects(newProjects);
        setOptions(response.data.options);
      } catch (error) {
        console.error(error.message);
      } finally {
        //ローディング画面非表示
        setIsLoading(false);
      }
    };

    // 検索パラメータの初期値を設定
    const new_params = Object.assign({}, search_params);
    if (location.search.length > 0) {
      // getパラメータをパースする
      const get_queries = parse(location.search.slice(1));
      // 検索パラメータの初期化状態にマージする
      Object.assign(new_params, get_queries);
      // パラメータを更新する
      setSearchParams(new_params);
    }

    // Ajaxリクエスト
    if (location.search.length > 0) {
      searchProject(new_params);
    } else {
      getProject();
    }
  }, []);

  /**
   * 検索項目変更時のイベントハンドラ
   *
   * @param {React.ChangeEvent} event イベント
   */
  const handleChangeSearchParams = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = event.target;
      setSearchParams(prev => {
        return { ...prev, [name]: value };
      });
    },
    [setSearchParams]
  );

  const handleChangeDate = useCallback((value: Date) => {
    if (value) {
      setSearchParams(prev => {
        return { ...prev, reception_date: Moment(value).format('YYYY-MM-DD') };
      });
    }
  }, []);

  // 検索処理
  const handleClickSearch = useCallback(() => {
    // クリックイベント
    const searchProjects = async () => {
      try {
        // ローディング画面表示
        setIsLoading(true);
        setIsConnecting(true);
        // バリデーション初期化
        setValidMessage({});

        const response = await axios.post(
          `${location.origin}${process.env.MIX_MOCK_URL}/api/projects/staff/search`,
          search_params
        );
        const newProjects: DatatableConvertedData[] = convertMDBDatatableRowData(
          response.data.projects
        );
        setProjects(newProjects);
      } catch (exception) {
        console.error(exception);
        if (exception.response.data.status_code === 422) {
          setValidMessage(exception.response.data.message);
        }

        if (exception.response.status === http_status_code_list.unauthorized) {
          // セッション切れの場合はログイン画面に遷移
          location.replace(exception.response.data.url);
        }
      } finally {
        setIsConnecting(false);
        //ローディング画面非表示
        setIsLoading(false);
      }
    };
    searchProjects();
  }, [search_params]);

  /**
   * 検索条件をクリアする
   */
  const handleClickClear = () => {
    setValidMessage({});
    setSearchParams({
      store_in_charge: store_in_charge,
      application_no: '',
      name: '',
      account_no: '',
      loan_type: '',
      reception_date: null,
      status: '',
      acceptance_or_disapproval: '0',
    });
  };

  /**
   * 検索項目フォーカスアウト時のイベントハンドラ
   *
   * @param {React.ChangeEvent} event イベント
   */
  const handleBlurSearchParams = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      // 名前(漢字)に半角スペースが混入している場合は全角スペースに修正する
      if (name === 'name') {
        setSearchParams(prev => {
          return { ...prev, [name]: value.replace(' ', '　') };
        });
      }
    },
    [setSearchParams]
  );

  return (
    <>
      <StaffNavbar user_role="staff" has_loan_aproval_system={false} />
      <div className="container-staff">
        <Row>
          <Col>
            <p className="mt-5">案件一覧</p>
          </Col>
        </Row>
        <div className="search-area">
          <SearchForm
            options={options}
            search_params={search_params}
            handleClickSearch={handleClickSearch}
            handleChangeSearchParams={handleChangeSearchParams}
            handleClickClear={handleClickClear}
            handleChangeDate={handleChangeDate}
            is_connecting={is_connecting}
            valid_message={valid_message}
            handleBlurSearchParams={handleBlurSearchParams}
          />
        </div>

        <h3>一覧</h3>
        <MDBDatatable datas={projects} />
      </div>
    </>
  );
};

if (document.getElementById('project-list-staff')) {
  render(<ProjectListStaff />, document.getElementById('project-list-staff'));
}
